import { Logo } from "@repo/ui/components/logo";
import { Badge } from "@repo/ui/components/ui/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@repo/ui/components/ui/sidebar";
import { Link } from "@tanstack/react-router";
import { LogOut, Moon, Sun } from "lucide-react";
import type { ReactNode } from "react";
import { navItems } from "./nav";

interface AppSidebarProps {
  children: ReactNode;
  name: string;
  viewingFor?: string;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  onLogout: () => void;
}

/**
 * Patient navigation. The shared Sidebar primitive is responsive on its own:
 * a persistent rail from `md` up, an off-canvas Sheet drawer on mobile (opened
 * via the header's SidebarTrigger). We only supply content.
 */
export function AppSidebar({ children, name, viewingFor, theme, onToggleTheme, onLogout }: AppSidebarProps) {
  return (
    <Sidebar>
      <SidebarHeader className="px-3 py-4">
        <Logo variant="adaptive" className="h-6 w-auto" title="MindForge" />
      </SidebarHeader>

      <SidebarContent>{children}</SidebarContent>

      <SidebarFooter>
        <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 text-sm text-blue-950 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-100">
          <div>
            <div className="text-xs text-blue-700 dark:text-blue-300">Signed in as</div>
            <div className="break-words font-medium">{name}</div>
          </div>
          {viewingFor && (
            <div>
              <div className="text-xs text-blue-700 dark:text-blue-300">On behalf of</div>
              <div className="break-words font-medium">{viewingFor}</div>
            </div>
          )}
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={onToggleTheme}>
              {theme === "light" ? <Moon /> : <Sun />}
              <span>{theme === "light" ? "Dark mode" : "Light mode"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={onLogout}>
              <LogOut />
              <span>Sign out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

export function NavGroup({ questionnaireCount }: { questionnaireCount: number }) {
  const { setOpenMobile } = useSidebar();

  // Collapse the mobile drawer after navigating; no-op on desktop.
  const closeOnMobile = () => setOpenMobile(false);

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {navItems.map((item) => (
            <SidebarMenuItem key={item.to}>
              <SidebarMenuButton
                render={
                  <Link
                    to={item.to}
                    activeProps={{ "data-active": "true" }}
                    activeOptions={{ exact: item.to === "/" }}
                    onClick={closeOnMobile}
                  />
                }
              >
                <item.icon />
                <span>{item.title}</span>
              </SidebarMenuButton>
              {item.to === "/questionnaires" && questionnaireCount > 0 && (
                <SidebarMenuBadge>
                  <Badge>{questionnaireCount}</Badge>
                </SidebarMenuBadge>
              )}
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
